import { wait } from '@idk-web/core-utils';
import { LocalStorage } from '@idk-web/core-ui';
import {
  CodeRequest,
  initInboxLogin,
  getInboxLoginStatus,
  completeInboxLogin,
  InboxLoginStatusResponse,
} from '@idk-web/api';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

type InitCallback = (
  data: Extract<InboxLoginStatusResponse, { status: 'IN_PROGRESS' }>,
) => void;
type CompleteCallback = (
  data: Extract<InboxLoginStatusResponse, { status: 'COMPLETED' }>,
) => void;
type ErrorCallback = (error: unknown) => void;
type ProgressCallback = (
  data: Extract<InboxLoginStatusResponse, { status: 'IN_PROGRESS' }>,
) => void;
type InitFunction = () => Promise<
  Extract<InboxLoginStatusResponse, { status: 'IN_PROGRESS' }>
>;
type CancelFunction = (id: string) => Promise<void>;
type GetStatusFunction = (id: string) => Promise<InboxLoginStatusResponse>;

export type PendingInboxLoginRequest = {
  onInit(callback: InitCallback): PendingInboxLoginRequest;
  onComplete(callback: CompleteCallback): PendingInboxLoginRequest;
  onError(callback: ErrorCallback): PendingInboxLoginRequest;
  onProgress(callback: ProgressCallback): PendingInboxLoginRequest;
  start(): StartedInboxLoginRequest;
};

export type StartedInboxLoginRequest = {
  wait(): Promise<void>;
  cancel(): void;
};

export function bankIdSe(): PendingInboxLoginRequest {
  return start(
    () => initInboxLogin({ method: 'BANKID_SE' }),
    async () => {
      // Not supported
    },
    getInboxLoginStatus,
  );
}

export function freja(): PendingInboxLoginRequest {
  return start(
    () => initInboxLogin({ method: 'FREJA' }),
    async () => {
      // Not supported
    },
    getInboxLoginStatus,
  );
}

export function bankIdNo(window: Window): PendingInboxLoginRequest {
  return startExternal(
    window,
    () => initInboxLogin({ method: 'BANKID_NO' }),
    completeInboxLogin,
  );
}

export function mitId(window: Window): PendingInboxLoginRequest {
  return startExternal(
    window,
    () => initInboxLogin({ method: 'MITID' }),
    completeInboxLogin,
  );
}

export function ftn(window: Window): PendingInboxLoginRequest {
  return startExternal(
    window,
    () => initInboxLogin({ method: 'FTN' }),
    completeInboxLogin,
  );
}

function startExternal(
  window: Window,
  init: () => Promise<
    Extract<InboxLoginStatusResponse, { status: 'IN_PROGRESS' }>
  >,
  login: (
    id: string,
    req: CodeRequest,
  ) => Promise<Extract<InboxLoginStatusResponse, { status: 'COMPLETED' }>>,
): PendingInboxLoginRequest {
  return start(
    async () => {
      const { id, url } = await init();
      window.location.href = url ?? '';
      return { status: 'IN_PROGRESS', id };
    },
    async () => window.close(),
    async (id) => {
      const code = LocalStorage.getItem('code');

      if (code) {
        LocalStorage.removeItem('code');
      } else if (window.closed) {
        return { status: 'FAILED', error: 'cancelled', id };
      } else {
        return { status: 'IN_PROGRESS', id };
      }

      return login(id, { code });
    },
  );
}

function start(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
): PendingInboxLoginRequest {
  const initCallbacks: InitCallback[] = [];
  const completeCallbacks: CompleteCallback[] = [];
  const errorCallbacks: ErrorCallback[] = [];
  const progressCallbacks: ProgressCallback[] = [];
  const controller = new AbortController();

  return {
    onInit(callback) {
      initCallbacks.push(callback);
      return this;
    },
    onComplete(callback) {
      completeCallbacks.push(callback);
      return this;
    },
    onError(callback) {
      errorCallbacks.push(callback);
      return this;
    },
    onProgress(callback) {
      progressCallbacks.push(callback);
      return this;
    },
    start() {
      const promise = run(
        init,
        cancel,
        getStatus,
        (data) => initCallbacks.forEach((f) => f(data)),
        (data) => completeCallbacks.forEach((f) => f(data)),
        (error) => errorCallbacks.forEach((f) => f(error)),
        (data) => progressCallbacks.forEach((f) => f(data)),
        controller,
      );

      return {
        wait: () => promise,
        cancel: () => controller.abort(),
      };
    },
  };
}

async function run(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
  onInit: InitCallback,
  onComplete: CompleteCallback,
  onError: ErrorCallback,
  onProgress: ProgressCallback,
  controller: AbortController,
): Promise<void> {
  let timeLeft = POLL_TIMEOUT_MS;
  let id: string;

  try {
    const response = await init();
    id = response.id;

    onInit(response);
  } catch (e) {
    onError(e);
    throw e;
  }

  controller.signal.addEventListener('abort', () => cancel(id));

  while (timeLeft > 0) {
    let response: InboxLoginStatusResponse;
    try {
      response = await getStatus(id);
    } catch (e: unknown) {
      onError(e);
      throw e;
    }

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }

    switch (response.status) {
      case 'FAILED':
        onError(response.error);
        throw new Error(response.error);
      case 'COMPLETED':
        onComplete(response);
        return;
    }

    timeLeft -= POLL_INTERVAL_MS;

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    } else {
      onProgress(response);
    }

    await wait(POLL_INTERVAL_MS);

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }
  }

  if (controller.signal.aborted) {
    onError('cancelled');
    throw new Error('cancelled');
  } else {
    onError('timed out');
    throw new Error('timed out');
  }
}
